import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ConnectionsPrint from "@/components/connections/ConnectionsPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { useConnectionsExport } from "@/hooks/useConnectionsExport";
import {
  Users,
  RefreshCw,
  Loader2,
  Search,
  Network,
  Sparkles,
  Trash2,
  Table as TableIcon,
} from "lucide-react";

const REMARK_PLUGINS = [remarkGfm];

export default function Connections({ source, onRegisterExport }) {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(null);
  const [limit, setLimit] = useState(500);
  const [connections, setConnections] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const printRef = useRef(null);
  const { exportCsv, exportPdf, printPage } = useConnectionsExport(printRef, contacts);

  // Register this tab's exporter so the context-aware header PDF/Print act on
  // the Connections content when this tab is active.
  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("connections", { exportPdf, printFn: printPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterExport, exportPdf, printPage, contacts.length]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // Contacts is the primary payload and must surface errors instead of
      // silently showing an empty "no contacts" state on timeout/failure.
      // Connections map + AI report are optional and stay graceful.
      const [c, conn, rep] = await Promise.all([
        api.getContacts(),
        api.getConnections().catch(() => null),
        api.getConnectionsReport().catch(() => ({ report_md: null })),
      ]);
      setContacts(c.contacts || []);
      setTotal(c.total);
      setLimit(c.limit || 500);
      setConnections(conn);
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load connections:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const syncContacts = async () => {
    setSyncing(true);
    try {
      const res = await api.syncContacts(limit);
      toast.success(`Captured ${res.count} contacts.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not capture contacts.");
    } finally {
      setSyncing(false);
    }
  };

  const generateReport = async () => {
    setReportLoading(true);
    try {
      await api.generateConnectionsReport();
      const start = Date.now();
      const poll = async () => {
        const st = await api.connectionsReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Relationship analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - start > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out. Please try again.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  const clearAll = async () => {
    try {
      await api.clearConnections();
      setContacts([]);
      setConnections(null);
      setReport(null);
      toast.success("Connections cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear.");
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      [c.name, c.company, c.phone, c.email].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [contacts, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const isCsv = source === "csv";
  const hasContacts = contacts.length > 0;

  return (
    <div className="space-y-6">
      {/* Header / controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Connections</h2>
            <p className="text-xs text-muted-foreground">
              {hasContacts
                ? `Showing ${contacts.length}${total ? ` of ${total.toLocaleString()}` : ""} captured contacts`
                : "No contacts captured yet"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Count</span>
            <Input
              data-testid={TID.contactsCountInput}
              type="number"
              min={1}
              max={2000}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(2000, Number(e.target.value) || 0)))}
              className="w-20 h-9"
              disabled={isCsv}
            />
            <Button
              data-testid={TID.syncContactsBtn}
              onClick={syncContacts}
              disabled={syncing || isCsv}
              variant="outline"
              size="sm"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1.5">Sync Contacts</span>
            </Button>
          </div>
          <div className="h-5 w-px bg-border mx-1 hidden sm:block" />
          <Button data-testid={TID.connCsvBtn} onClick={exportCsv} disabled={!hasContacts} variant="outline" size="sm">
            <TableIcon className="w-4 h-4" />
            <span className="ml-1.5 hidden sm:inline">CSV</span>
          </Button>
          <Button data-testid={TID.connClearBtn} onClick={clearAll} disabled={!hasContacts} variant="outline" size="sm" className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4" />
            <span className="ml-1.5 hidden sm:inline">Clear</span>
          </Button>
        </div>
      </div>

      {isCsv && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 px-4 py-2.5 text-sm text-foreground/80">
          Contact capture is available only for live HubSpot connections. CSV uploads contain deals only.
        </div>
      )}

      {!hasContacts && !isCsv ? (
        <div className="text-center py-16 text-muted-foreground">
          No contacts captured. Set a count and click <span className="font-medium">Sync Contacts</span> to
          capture up to 2,000 contacts from HubSpot.
        </div>
      ) : (
        hasContacts && (
          <>
            {/* Contact grid */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" /> Contact List
                  </CardTitle>
                  <div className="relative w-64 max-w-full">
                    <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-2.5" />
                    <Input
                      data-testid={TID.contactsFilter}
                      placeholder="Filter by name, company, phone, email…"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[420px] overflow-y-auto" data-testid={TID.contactsGrid}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium text-foreground">{c.name}</TableCell>
                          <TableCell className="text-muted-foreground">{c.company || "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{c.phone || "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{c.email || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {filtered.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                            No contacts match "{filter}".
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Relationship map */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Network className="w-4 h-4 text-primary" /> Relationship Map
                  {connections && (
                    <span className="text-xs font-normal text-muted-foreground ml-1">
                      · {connections.cluster_count} clusters · {connections.orphan_contacts} standalone
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {connections && connections.clusters?.length ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={TID.connectionsMap}>
                    {connections.clusters.map((g) => (
                      <div key={`${g.type}:${g.label}`} className="rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-foreground truncate">{g.label}</span>
                          <span className="text-xs text-primary font-medium ml-2">{g.count}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
                          {g.type === "company" ? "Company" : "Email domain"}
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {g.contacts.slice(0, 6).map((ct) => (
                            <span key={ct.id} className="text-[11px] bg-muted rounded px-1.5 py-0.5 text-foreground/80">
                              {ct.name}
                            </span>
                          ))}
                          {g.count > 6 && (
                            <span className="text-[11px] text-muted-foreground">+{g.count - 6} more</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-6" data-testid={TID.connectionsMap}>
                    No relationship clusters found — contacts don't share a common company or email domain.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI relationship report */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" /> AI Relationship Analysis
                  </CardTitle>
                  <Button
                    data-testid={TID.genConnectionsReportBtn}
                    onClick={generateReport}
                    disabled={reportLoading}
                    variant="outline"
                    size="sm"
                  >
                    {reportLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" /> {report ? "Regenerate" : "Generate Analysis"}
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {report ? (
                  <article
                    data-testid={TID.connectionsReportContent}
                    className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-h2:mt-5 prose-strong:text-foreground"
                  >
                    <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
                  </article>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Generate an AI analysis that maps relationships across your contacts (key accounts,
                    coverage gaps, and recommendations) — powered by Claude Sonnet 4.5.
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )
      )}

      {/* Off-screen print/PDF layout */}
      <ConnectionsPrint ref={printRef} contacts={contacts} connections={connections} reportMd={report} />
    </div>
  );
}
